import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { DateTime } from 'luxon';
import type { LogistikProductCategory, LogistikShippingStatus } from '../enums/LogistikEnums.js';
import { LogistikOrderClearanceState } from '../LogistikOrder.js';
import { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';

export const CLEARANCE_FROM_ALL = 'Alle Logistikmitarbeiter';

@Entity()
export class LogistikOrderFilter extends AdornisFilter {
  public static override _class = 'HubOrderFilter'; // TODO needs migration before we can change this

  @Field(() => [String], { default: v => v ?? null })
  public status: Maybe<LogistikShippingStatus[]>;

  @Field(() => DateTime, { default: v => v ?? null })
  public dateFrom?: DateTime;

  @Field(() => DateTime, { default: v => v ?? null })
  public dateTo?: DateTime;

  @Field(() => String, { default: v => v ?? null })
  public productCategory?: LogistikProductCategory;

  @Field(() => Boolean, { default: v => v ?? false })
  needsClearance?: boolean;

  @Field(() => Boolean, { default: v => v ?? false })
  declinedClearance?: boolean;

  @Field(() => [String], { default: v => v ?? ['createdAt', '_id'] })
  public override sortingFields!: string[];

  @Field(() => [String], { default: v => v ?? ['desc' as 'desc' | 'asc', 'desc' as 'desc' | 'asc'] })
  public override sortingDirections!: Array<'desc' | 'asc'>;

  override async getMongoFilter(): Promise<Dictionary<any>> {
    return { $and: [{ _class: 'HubOrder' }, ...(await this.getFilterArray())] };
  }

  override getMongoOptions() {
    const opts = super.getMongoOptions();
    if (Object.keys(opts.sort).length === 0) {
      opts.sort.createdAt = -1;
      opts.sort._id = -1;
    }
    return opts;
  }

  async getFilterArray(): Promise<Array<Dictionary<any>>> {
    const filter = this.toObject();

    const filterStatus = filter.status ? { status: { $in: filter.status } } : {};

    const filterDateFrom = filter.dateFrom ? { createdAt: { $gte: filter.dateFrom } } : {};

    const filterDateTo = filter.dateTo ? { createdAt: { $lte: filter.dateTo } } : {};

    const filterProductCategory = filter.productCategory
      ? { items: { $elemMatch: { productCategory: filter.productCategory } } }
      : {};

    const filterClearance = filter.declinedClearance
      ? { clearanceState: LogistikOrderClearanceState.DECLINED }
      : !filter.needsClearance
        ? {
            $or: [
              { clearanceState: { $exists: false } },
              { clearanceState: null },
              { clearanceState: LogistikOrderClearanceState.APPROVED },
            ],
          }
        : { clearanceState: LogistikOrderClearanceState.NEEDS_CLEARANCE };

    let filterClearanceFrom = {};

    if (!context.serverContext) {
      const userCollection = await getRawCollection<EntityData<ChancenwerkUser>>(ChancenwerkUser._collectionName);
      const user = await userCollection.findOne({ _id: context.userID });

      filterClearanceFrom =
        filter.needsClearance && !filter.declinedClearance
          ? {
              $or: [
                { clearanceFrom: { $elemMatch: { $eq: user ? user.email : 'NO_ACCESS_TO_CLEARANCE_ITEMS' } } },
                { clearanceFrom: { $elemMatch: { $eq: CLEARANCE_FROM_ALL } } },
              ],
            }
          : {};
    }

    return [filterStatus, filterDateFrom, filterDateTo, filterProductCategory, filterClearance, filterClearanceFrom];
  }
}
